/**
 * TTS読み方辞書管理画面
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTtsPronunciationDictionary,
  createTtsPronunciationDictionaryEntry,
  updateTtsPronunciationDictionaryEntry,
  deleteTtsPronunciationDictionaryEntry,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface TtsPronunciationDictionaryEntry {
  id: number;
  original_text: string;
  pronunciation_text: string;
  description?: string;
  created: string;
  updated: string;
}

export default function TtsPronunciationDictionary() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TtsPronunciationDictionaryEntry | null>(null);
  const [formData, setFormData] = useState({
    original_text: '',
    pronunciation_text: '',
    description: '',
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'tts-pronunciation-dictionary'],
    queryFn: () => getTtsPronunciationDictionary(),
  });

  const createMutation = useMutation({
    mutationFn: createTtsPronunciationDictionaryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-pronunciation-dictionary'] });
      setIsCreateDialogOpen(false);
      setFormData({ original_text: '', pronunciation_text: '', description: '' });
      toast({
        title: '成功',
        description: '読み方辞書エントリを作成しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '読み方辞書エントリの作成に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateTtsPronunciationDictionaryEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-pronunciation-dictionary'] });
      setIsEditDialogOpen(false);
      setSelectedEntry(null);
      setFormData({ original_text: '', pronunciation_text: '', description: '' });
      toast({
        title: '成功',
        description: '読み方辞書エントリを更新しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '読み方辞書エントリの更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTtsPronunciationDictionaryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-pronunciation-dictionary'] });
      setIsDeleteDialogOpen(false);
      setSelectedEntry(null);
      toast({
        title: '成功',
        description: '読み方辞書エントリを削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '読み方辞書エントリの削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleCreate = () => {
    if (!formData.original_text || !formData.pronunciation_text) {
      toast({
        title: 'エラー',
        description: '元のテキストと読み方は必須です',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate({
      original_text: formData.original_text,
      pronunciation_text: formData.pronunciation_text,
      description: formData.description || undefined,
    });
  };

  const handleEdit = (entry: TtsPronunciationDictionaryEntry) => {
    setSelectedEntry(entry);
    setFormData({
      original_text: entry.original_text,
      pronunciation_text: entry.pronunciation_text,
      description: entry.description || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedEntry) return;

    if (!formData.original_text || !formData.pronunciation_text) {
      toast({
        title: 'エラー',
        description: '元のテキストと読み方は必須です',
        variant: 'destructive',
      });
      return;
    }

    updateMutation.mutate({
      id: selectedEntry.id,
      data: {
        original_text: formData.original_text,
        pronunciation_text: formData.pronunciation_text,
        description: formData.description || undefined,
      },
    });
  };

  const handleDelete = (entry: TtsPronunciationDictionaryEntry) => {
    setSelectedEntry(entry);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedEntry) {
      deleteMutation.mutate(selectedEntry.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">TTS読み方辞書管理</h1>
            <p className="text-base text-gray-500 mt-2">TTS音声合成時の読み方辞書データの管理</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>新規読み方辞書エントリ作成</DialogTitle>
                <DialogDescription>新しい読み方辞書エントリを追加します</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="create-original-text">元のテキスト（必須）</Label>
                  <Input
                    id="create-original-text"
                    value={formData.original_text}
                    onChange={(e) => setFormData({ ...formData, original_text: e.target.value })}
                    placeholder="例: 歯間ブラシ"
                  />
                </div>
                <div>
                  <Label htmlFor="create-pronunciation-text">読み方（必須）</Label>
                  <Input
                    id="create-pronunciation-text"
                    value={formData.pronunciation_text}
                    onChange={(e) => setFormData({ ...formData, pronunciation_text: e.target.value })}
                    placeholder="例: しかんぶらし"
                  />
                </div>
                <div>
                  <Label htmlFor="create-description">説明（任意）</Label>
                  <Textarea
                    id="create-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="例: 歯間ブラシの読み方"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsCreateDialogOpen(false);
                  setFormData({ original_text: '', pronunciation_text: '', description: '' });
                }}>
                  キャンセル
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* 読み方辞書一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">読み方辞書一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">元のテキスト</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">読み方</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">説明</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.entries?.map((entry: TtsPronunciationDictionaryEntry) => (
                  <TableRow key={entry.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{entry.id}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 text-center">{entry.original_text}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 text-center">{entry.pronunciation_text}</TableCell>
                    <TableCell className="py-4 text-base text-gray-700 text-center">{entry.description || '-'}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">
                      {new Date(entry.created).toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => handleEdit(entry)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          aria-label="編集"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="削除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>読み方辞書エントリ編集</DialogTitle>
              <DialogDescription>読み方辞書エントリを編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-original-text">元のテキスト（必須）</Label>
                <Input
                  id="edit-original-text"
                  value={formData.original_text}
                  onChange={(e) => setFormData({ ...formData, original_text: e.target.value })}
                  placeholder="例: 歯間ブラシ"
                />
              </div>
              <div>
                <Label htmlFor="edit-pronunciation-text">読み方（必須）</Label>
                <Input
                  id="edit-pronunciation-text"
                  value={formData.pronunciation_text}
                  onChange={(e) => setFormData({ ...formData, pronunciation_text: e.target.value })}
                  placeholder="例: しかんぶらし"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">説明（任意）</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="例: 歯間ブラシの読み方"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedEntry(null);
                setFormData({ original_text: '', pronunciation_text: '', description: '' });
              }}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>読み方辞書エントリを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。読み方辞書エントリID「{selectedEntry?.id}」（{selectedEntry?.original_text} → {selectedEntry?.pronunciation_text}）を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
